import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount, usePublicClient } from 'wagmi'
import {
  TransactionCallback,
  TransactionCallbackStage,
} from '@lidofinance/lido-ethereum-sdk'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import {
  useLidoSDK,
  useStEthBalance,
  useStEth2WstEthRate,
  useWrapGasFee,
  useIsStEthNeedApprove,
  useWrapReceiveAmount,
  useIsValidNetwork,
} from '../hooks'
import {
  useModalState,
  useModalToggle,
  useApprovalModalStateDispatch,
  useBlockNumber,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import { parseUnits } from 'viem'
import TokenInput from '../components/TokenInput'
import PrimaryButton from '~/components/Button/PrimaryButton'
import OrderProgress from '../components/OrderProgress'
import MaxButton from '~/components/Button/MaxButton'
import { ETH, stETH, wstETH } from '~/constants/tokens'
import { formatFormAmount, toBigNumber, gt } from '~/utils/numbers'
import { RESET_STAGE_TIME } from '../constants'
import {
  StyledStakeSection,
  StyledStakeDescription,
  StyledStakeForm,
  StyledFormGroup,
  StyledLabel,
  StyledBalanceInfo,
  StyledReceiveInfo,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
} from '../styled'
// import ImgLidoWrapFlow from '~/assets/images/lido-wrap-flow.png'
import ImgTokenWstETH from '~/assets/images/token-wsteth-with-bg.png'
import NotificationBar from '~/components/NotificationBar'
import { useAppDispatch } from '~/state/hooks'
import { updateBlockNumber } from '~/state/application/reducer'
import { ApprovalStatus } from '../types'
import styled from 'styled-components'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'
import { getIsUserCancel } from '../utils'

const StyledWrapFlowImgContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const WrapForm = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const { address: account, chainId } = useAccount()
  const publicClient = usePublicClient()
  const isWalletConnected = !!account
  const [amount, setAmount] = useState('')
  const [
    currentStage,
    setCurrentStage,
  ] = useState<TransactionCallbackStage | null>(null)
  const [showOrderProgress, setShowOrderProgress] = useState(false)
  // const [isOpenTokenList, setIsOpenTokenList] = useState(false)
  // const [selectedToken, setSelectedToken] = useState<BaseTokenlonToken>(stETH)
  const selectedToken = stETH
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('idle')
  const ethBalance = useNativeTokenBalance()
  const formattedEthBalance = ethBalance?.toDecimalsFixed()
  const stEthBalance = useStEthBalance()
  const formattedStEthBalance = stEthBalance?.toDecimalsFixed()
  const isSelectedETH = selectedToken.address === ETH.address
  const isValidNetwork = useIsValidNetwork()
  const dispatch = useAppDispatch()
  const latestBlockNumber = useBlockNumber()

  const { wrap } = useLidoSDK()
  const stEth2WstEthRate = useStEth2WstEthRate('1')
  const { gasFee, gasFeeUSD } = useWrapGasFee(isSelectedETH, amount)
  const receiveAmount = useWrapReceiveAmount(amount)

  const isStEthNeedApprove = useIsStEthNeedApprove(isSelectedETH ? '0' : amount)

  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const toggleAllowanceModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('toggleAllowanceModal.approve_title', {
          symbol: stETH.symbol,
        }),
        description1: t('toggleAllowanceModal.description_1'),
        description2: t('toggleAllowanceModal.description_2'),
        showLogo: false,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'lido_wrap' },
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const selectedTokenBalance = useMemo(() => {
    if (isSelectedETH) {
      return formattedEthBalance
    }
    return formattedStEthBalance
  }, [selectedToken, formattedEthBalance, formattedStEthBalance])

  const isInsufficientBalance = useMemo(() => {
    return gt(amount, selectedTokenBalance)
  }, [amount, selectedTokenBalance])

  const isInsufficientETHForGas = useMemo(() => {
    if (!amount || !formattedEthBalance || !gasFee) {
      return false
    }
    const ethBalanceBN = toBigNumber(formattedEthBalance)
    const totalCost = isSelectedETH
      ? toBigNumber(amount).plus(toBigNumber(gasFee))
      : toBigNumber(gasFee)
    return gt(totalCost, ethBalanceBN)
  }, [amount, formattedEthBalance, gasFee, isSelectedETH])

  const handleMaxClick = () => {
    let maxAmount = selectedTokenBalance
    if (isSelectedETH && selectedTokenBalance && gasFee) {
      const ethBalance = toBigNumber(selectedTokenBalance)
      const remainingBalance = ethBalance.minus(gasFee)
      maxAmount = remainingBalance.gt(0) ? remainingBalance.toFixed() : '0'
    }
    // 当数量变化时重置授权状态
    if (maxAmount !== amount && approvalStatus !== 'idle') {
      setApprovalStatus('idle')
    }
    setAmount(maxAmount)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // 最多输入 6 位小数
    if (value === '' || /^(\d+)(\.\d{0,6})?$/.test(value)) {
      // 当数量变化时重置授权状态
      if (value !== amount && approvalStatus !== 'idle') {
        setApprovalStatus('idle')
      }
      setAmount(value)
    }
  }

  const handleBlur = () => {
    if (!amount) return
    // 去掉前导零
    let [int, dec] = amount.split('.')
    int = String(Number(int))
    setAmount(dec !== undefined ? `${int}.${dec}` : int)
  }

  const wrapValueRef = useRef<string>('0')
  const showOrderProgressRef = useRef(showOrderProgress)
  showOrderProgressRef.current = showOrderProgress

  const approveCallback: TransactionCallback = ({ stage }) => {
    if (stage === TransactionCallbackStage.RECEIPT) {
      updateApprovalState({
        progress: ApprovalProgress.APPROVE_SUCCESS,
      })
      // 等待交易确认
      setApprovalStatus('approving')
    }
    if (
      stage === TransactionCallbackStage.DONE ||
      stage === TransactionCallbackStage.MULTISIG_DONE
    ) {
      trackClick('webtl_stk_wrap_approve', {
        symbol: stETH.symbol,
        is_success: true,
      })
      // 手动触发区块号更新，尽快让 useTokenAllowance 获取到最新的授权状态
      if (chainId && publicClient) {
        publicClient
          .getBlockNumber()
          .then((blockNumber) => {
            if (blockNumber > latestBlockNumber) {
              dispatch(updateBlockNumber({ chainId, blockNumber }))
            }
          })
          .catch((error) => {})
      }
      setApprovalStatus('completed')
      setCurrentStage(null)
    }
  }

  const txCallback: TransactionCallback = ({ stage }) => {
    const targetStage = [
      TransactionCallbackStage.RECEIPT,
      TransactionCallbackStage.DONE,
      TransactionCallbackStage.MULTISIG_DONE,
      TransactionCallbackStage.ERROR,
    ]
    if (targetStage.includes(stage)) {
      setCurrentStage(stage)
    }

    switch (stage) {
      case TransactionCallbackStage.RECEIPT:
        trackClick('webtl_stk_wrap_submit', {
          symbol: isSelectedETH ? ETH.symbol : stETH.symbol,
          amount: wrapValueRef.current,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
        setShowOrderProgress(true)
        break
      case TransactionCallbackStage.DONE:
      case TransactionCallbackStage.MULTISIG_DONE:
        // 使用 Safe Wallet 时没有 RECEIPT 阶段，直接从 SIGN 阶段跳到 MULTISIG_DONE 阶段
        if (
          progress !== ApprovalProgress.APPROVE_SUCCESS &&
          !showOrderProgressRef.current
        ) {
          trackClick('webtl_stk_wrap_submit', {
            symbol: selectedToken.symbol,
            amount: wrapValueRef.current,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_SUCCESS,
          })
          setShowOrderProgress(true)
        }
        trackClick('webtl_stk_wrap_confirm', {
          symbol: selectedToken.symbol,
          amount: wrapValueRef.current,
          is_success: true,
        })
        wrapValueRef.current = '0'

        setTimeout(() => {
          setAmount('')
          setShowOrderProgress(false)
          setCurrentStage(null)
        }, RESET_STAGE_TIME)
        break
      default:
        break
    }
  }

  const handleWrap = async () => {
    setCurrentStage(TransactionCallbackStage.SIGN)
    const value = parseUnits(amount, selectedToken.decimals)

    if (!isSelectedETH && isStEthNeedApprove && approvalStatus === 'idle') {
      toggleAllowanceModal()

      try {
        await wrap.approveStethForWrap({
          value,
          callback: approveCallback,
        })
      } catch (error) {
        const isUserCancel = getIsUserCancel(error)
        if (!isUserCancel) {
          trackClick('webtl_stk_wrap_approve', {
            symbol: selectedToken.symbol,
            is_success: false,
            message: error.message,
          })
        }
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
        if (approvalStatus !== 'idle') {
          setApprovalStatus('idle')
        }
        setCurrentStage(null)
      }
      return
    }

    try {
      wrapValueRef.current = amount
      toggleSubmitParamsModal()

      if (isSelectedETH) {
        await wrap.wrapEth({
          value,
          callback: txCallback,
        })
      } else {
        await wrap.wrapSteth({
          value,
          callback: txCallback,
        })
      }
    } catch (error) {
      const isUserCancel = getIsUserCancel(error)
      if (isUserCancel) {
        trackView('webtl_stk_wrap_confirm_cancel')
      } else {
        trackClick('webtl_stk_wrap_confirm', {
          symbol: isSelectedETH ? ETH.symbol : stETH.symbol,
          amount: wrapValueRef.current,
          is_success: false,
          message: error.message,
        })
      }
      wrapValueRef.current = '0'
      updateApprovalState({
        progress: ApprovalProgress.APPROVE_REJECTED,
      })
      setTimeout(() => {
        setShowOrderProgress(false)
        setCurrentStage(null)
      }, RESET_STAGE_TIME)
    }
  }

  // const handleSelectToken = (token: BaseTokenlonToken) => {
  //   setSelectedToken(token)
  //   setIsOpenTokenList(false)
  //   // 重置授权状态当切换 token 时
  //   setApprovalStatus('idle')
  // }

  const handleConnectWallet = () => {
    trackClick('webtl_walletconnect', {
      from: 'lido_wrap',
    })
    toggleWalletModal()
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else if (!isWalletConnected) {
      handleConnectWallet()
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handleWrap()
    }
  }

  const buttonText = useMemo(() => {
    if (!isWalletConnected) {
      return t('connect_wallet')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isInsufficientBalance) {
      return t('preview_$symbol_insufficient_balance', {
        symbol: selectedToken.symbol,
      })
    }
    if (isInsufficientETHForGas) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    // 当授权状态为 approving 时，显示授权中
    if (
      currentStage !== null &&
      !isSelectedETH &&
      approvalStatus === 'approving'
    ) {
      return t('authorizing')
    }
    if (currentStage === null) {
      if (!isSelectedETH && isStEthNeedApprove && approvalStatus === 'idle') {
        return t('approve_token', { symbol: stETH.symbol })
      } else {
        return t('wrap')
      }
    }
    return t('waiting_for_confirm')
  }, [
    isWalletConnected,
    isValidNetwork,
    currentStage,
    isStEthNeedApprove,
    isSelectedETH,
    isInsufficientBalance,
    isInsufficientETHForGas,
    approvalStatus,
    t,
  ])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!Number(amount) ||
        isInsufficientBalance ||
        isInsufficientETHForGas ||
        currentStage === TransactionCallbackStage.SIGN)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    amount,
    currentStage,
  ])

  const showApproveButton = useMemo(
    () =>
      isStEthNeedApprove &&
      approvalStatus === 'idle' &&
      !isInsufficientBalance &&
      !isInsufficientETHForGas,
    [
      isStEthNeedApprove,
      approvalStatus,
      isInsufficientBalance,
      isInsufficientETHForGas,
    ],
  )

  return (
    <StyledStakeSection>
      <StyledStakeDescription>
        {/* <img src={ImgLidoWrapFlow.src} width={80} height={60} alt="wrap flow" /> */}
        <StyledWrapFlowImgContainer>
          <img src={stETH.logo} width={28} height={28} alt="stETH logo" />
          <span>→</span>
          <img src={wstETH.logo} width={28} height={28} alt="wstETH logo" />
        </StyledWrapFlowImgContainer>
        <div className="description-text">{t('lido_wrap_description')}</div>
      </StyledStakeDescription>

      <StyledStakeForm>
        <StyledFormGroup>
          {isWalletConnected && (
            <StyledLabel>
              <StyledBalanceInfo>
                {`${t('balance')}: ${formatFormAmount(
                  selectedTokenBalance ?? '~',
                  4,
                )} ${selectedToken.symbol}`}
                <MaxButton handleClick={handleMaxClick} />
              </StyledBalanceInfo>
            </StyledLabel>
          )}
          <TokenInput
            amount={amount}
            token={selectedToken}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
          />
        </StyledFormGroup>

        <StyledReceiveInfo>
          <StyledInfoRow>
            <StyledInfoLabel>{t('order_receive')}</StyledInfoLabel>
            <StyledInfoValue>
              {receiveAmount} wstETH{' '}
              <img
                src={ImgTokenWstETH.src}
                alt="wstETH"
                width={20}
                height={20}
              />
            </StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>{t('rate')}</StyledInfoLabel>
            <StyledInfoValue>
              {`1 ${selectedToken.symbol} = ${stEth2WstEthRate} wstETH`}
            </StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>{t('estimate_gas_fee')}</StyledInfoLabel>
            <StyledInfoValue>{`~ $${gasFeeUSD}`}</StyledInfoValue>
          </StyledInfoRow>
        </StyledReceiveInfo>

        {showOrderProgress && <OrderProgress stage={currentStage!} />}
        {!showOrderProgress && (
          <>
            {!isValidNetwork && (
              <NotificationBar
                level="warning"
                text={t('unmatched_network_swap_warning')}
                showLink={false}
                showClose={false}
              />
            )}
            <PrimaryButton
              text={buttonText}
              disabled={buttonDisabled}
              needApprove={showApproveButton}
              loading={currentStage === TransactionCallbackStage.SIGN}
              handleClick={handleClick}
            />
          </>
        )}
      </StyledStakeForm>
      {/* <TokenListModal
        tokens={formattedTokens}
        isOpen={isOpenTokenList}
        onDismiss={() => setIsOpenTokenList(false)}
        onSelect={handleSelectToken}
      /> */}
    </StyledStakeSection>
  )
}

export default WrapForm
